import React from 'react';
import { ActionIcon, Checkbox, Image, Table } from '@mantine/core';
import PropTypes from 'prop-types';
import { Dots } from 'tabler-icons-react';
import AppStack from './AppStack';
import AppFlexbox from './AppFlexbox';
import AppText from './AppText';
import classes from '../../styles/nestedStyles.module.css';
import MenuContentActionsList from './MenuContentActionsList';
import MenuContent from './MenuContent';

const TableRow = ({
  row,
  isTabletOrSmaller,
  onSelect,
  onAction,
  rowsSelected,
  minHeight,
  selectInputOnly
}) => {
  const renderTableColumnInfo = (
    rowKey,
    c,
    columnIndex,
    isMobile = false,
    hideIcon = false
  ) => (
    <AppFlexbox
      style={{ alignItems: 'center', gap: 10, minHeight: minHeight || 36 }}
    >
      {onSelect && columnIndex === 0 ? (
        <Checkbox
          checked={rowsSelected.includes(rowKey)}
          onChange={() => {
            if (selectInputOnly) {
              onSelect(rowKey);
            }
          }}
          styles={{ input: { cursor: 'pointer' } }}
        />
      ) : (
        onSelect && isMobile && <AppStack style={{ width: 20 }} />
      )}
      {!hideIcon &&
        (c.src || c.showImage ? (
          <AppStack style={{ width: 40, height: 40 }}>
            <Image
              fit="contain"
              h="100%"
              src={c.src}
              style={{ flex: 1 }}
              w="100%"
            />
          </AppStack>
        ) : (
          !!c.icon && <AppStack>{c.icon}</AppStack>
        ))}
      <AppStack
        component={c.component}
        style={{
          gap: 0,
          alignItems: 'start',
          textDecoration: 'none',
          color: '#000'
        }}
        to={c.to}
      >
        {c.customComponent ? (
          c.customComponent
        ) : (
          <>
            <AppText
              className={
                c.onClick || c.to ? classes['hover-text-action'] : null
              }
              color={c.color || '#000'}
              onClick={c.onClick}
              style={{
                fontSize: 14,
                lineHeight: '18px',
                ...(isMobile
                  ? {
                      display: '-webkit-box',
                      WebkitLineClamp: '2',
                      lineClamp: '2',
                      WebkitBoxOrient: 'vertical'
                    }
                  : { maxWidth: 400 })
              }}
              weight={c.weight}
            >
              {c.label}
            </AppText>
            {c.subLabel && (
              <AppText
                className={
                  c.onSubLabelClick ? classes['hover-text-action'] : null
                }
                color={c.subLabelColor || 'grey'}
                onClick={c.onSubLabelClick}
                style={{
                  fontSize: 14,
                  lineHeight: '18px',
                  ...(isMobile
                    ? {
                        display: '-webkit-box',
                        WebkitLineClamp: '2',
                        lineClamp: '2',
                        WebkitBoxOrient: 'vertical'
                      }
                    : { maxWidth: 400 })
                }}
              >
                {c.subLabel}
              </AppText>
            )}
          </>
        )}
      </AppStack>
    </AppFlexbox>
  );

  return isTabletOrSmaller ? (
    <Table.Tr>
      <Table.Td style={{ padding: '12px 10px' }}>
        <AppFlexbox style={{ flex: 1, alignItems: 'center' }}>
          <AppFlexbox
            onClick={
              onSelect && !selectInputOnly
                ? (e) => {
                    e.stopPropagation();
                    onSelect(row.key);
                  }
                : null
            }
            style={{
              flex: 1,
              alignItems: 'start',
              gap: 10,
              ...(onSelect && !selectInputOnly ? { cursor: 'pointer' } : {})
            }}
          >
            <AppStack
              style={{
                flex: 1,
                gap: 0,
                textWrap: 'wrap',
                alignSelf: 'center'
              }}
            >
              {renderTableColumnInfo(row.key, row.columns[0], 0, true)}
              {row.columns
                .filter((c) => c.showOnMobile)
                .map((c) => (
                  <React.Fragment key={c.key}>
                    {renderTableColumnInfo(row.key, c, null, true, true)}
                  </React.Fragment>
                ))}
            </AppStack>
          </AppFlexbox>

          {onAction && row.actions && (
            <MenuContent
              control={
                <ActionIcon color="lightgrey" radius="xl" variant="subtle">
                  <Dots color="#000" />
                </ActionIcon>
              }
              styles={{ dropdown: { padding: 0 } }}
            >
              <MenuContentActionsList
                actions={row.actions}
                data={row.data}
                onAction={(a) => onAction(a, row)}
              />
            </MenuContent>
          )}
        </AppFlexbox>
      </Table.Td>
    </Table.Tr>
  ) : (
    <Table.Tr>
      <>
        {row.columns.map((c, i) => (
          <Table.Td
            key={c.key}
            onClick={
              onSelect && !selectInputOnly
                ? (e) => {
                    e.stopPropagation();
                    onSelect(row.key);
                  }
                : null
            }
            style={onSelect && !selectInputOnly ? { cursor: 'pointer' } : {}}
          >
            {renderTableColumnInfo(row.key, c, i)}
          </Table.Td>
        ))}
        {onAction && (
          <Table.Td style={{ textAlign: 'end', paddingRight: 40 }}>
            {row.actions && (
              <MenuContent
                control={
                  <ActionIcon color="lightgrey" radius="xl" variant="subtle">
                    <Dots color="#000" />
                  </ActionIcon>
                }
                styles={{ dropdown: { padding: 0 } }}
              >
                <MenuContentActionsList
                  actions={row.actions}
                  data={row.data}
                  onAction={(a) => onAction(a, row)}
                />
              </MenuContent>
            )}
          </Table.Td>
        )}
      </>
    </Table.Tr>
  );
};

TableRow.propTypes = {
  index: PropTypes.number.isRequired,
  row: PropTypes.object.isRequired,
  isTabletOrSmaller: PropTypes.bool,
  minHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onAction: PropTypes.func,
  onSelect: PropTypes.func,
  rowsSelected: PropTypes.array,
  selectInputOnly: PropTypes.bool
};

export default TableRow;
